
import { Component, Mixins, Prop } from "vue-property-decorator"
import { Getter, namespace, State } from "vuex-class"

import vSelect from "vue-select"

import CaseLinks from "@/components/CaseDetails/CaseLinks.vue"
import CaseUsers from "@/components/CaseDetails/CaseUsers.vue"
import Invite from "@/components/CaseDetails/Users/Invite.vue"
import CaseActions from "@/components/CaseDetails/CaseActions.vue"
import CaseFields from "./CaseFields.vue"
import CaseMergeModal from "@/components/CaseDetails/CaseMergeModal.vue"
import Tags from "@/components/Fields/Tags.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"
import UserImage from "@/components/UserImage.vue"

import CaseFile from "@/types/caseFile"

import { Area, CaseDetails, CaseField, Schedule, Severity, Status, TemplateDetails, TimeZone } from "@/types"
import { CaseEvent, CaseEvents } from "@/types/originatingEvents"
import { CurrentUser, UserWithFields } from "@/types/user"

import api from "@/services/api"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import { BModal } from "bootstrap-vue"

import { LayoutLocations } from "@/types/templateLayout"

import { IntegrationOutOption } from "@/types/integration"
import { FormatFieldsMixin, formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { TenantFeatures } from "@/types/tenantFeatures"
import { SourceType } from "@/types/sourceType"
import { CadenceInstance } from "@/types/cadence"
import { LabeledValue } from "@/types/labeledValue"
import cloneDeep from "lodash/cloneDeep"
import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"
import { CaseUser, UserRole, UserRoleDefinition } from "@/types/caseUser"
import CaseSchedule from "./CaseSchedule.vue"
import { ScheduleType } from "@/types/schedule"

const caseStore = namespace("cases")

@Component({
	components: {
		"user-image": UserImage,
		"case-users": CaseUsers,
		"tags": Tags,
		"v-select": vSelect,
		"invite-user": Invite,
		"case-links": CaseLinks,
		"case-actions": CaseActions,
		"area-select": AreaSelect,
		"case-fields": CaseFields,
		"loader": SkeletonLoader,
		"case-merge-modal": CaseMergeModal,
		"case-schedule": CaseSchedule
	}
})
export default class CaseSideBar extends Mixins(FormatFieldsMixin) {
	$refs!: {
		confirmCloseModal: BModal
		caseMergeModal: CaseMergeModal,
	}

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@caseStore.Getter("getStatuses") public statuses!: Status[];
	@caseStore.Getter("getSeverities") public severities!: Severity[];
	@caseStore.State("areas") public areas!: Area[];

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents | null;

	@caseStore.Action public caseOpen!: (caseId: string) => void;

	@State
	public me!: CurrentUser;

	@caseStore.Mutation
	public setShowCCEvent!: (ccEvent: CaseEvent) => void;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Prop({ default: false })
	canEditCurrentCase!: boolean;

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public template!: TemplateDetails

	@Prop()
	public caseFiles!: CaseFile[];

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: false })
	editAfterClosePermissions!: boolean

	@Prop({ default: false })
	canMakeClosedChanges!: boolean

	@Prop({ default: () => [] })
	caseIntegrationLinks!: IntegrationOutOption[]

	@Prop({ default: null })
	caseCadenceInstance!: CadenceInstance | null

	LayoutLocations = LayoutLocations

	closeModalShown: boolean = false
	showScheduleModal: boolean = false

	public schedule: Schedule | null = null

	copiedItems = ""

	TenantFeatures = TenantFeatures

	async openScheduleModal () {
		if (this.currentCase.scheduling?.scheduleId) {
			this.showScheduleModal = true
		}
	}

	private caseUpdating = false
	private caseUpdateQueued = false

	public get anyUserRoles () {
		if (!this.currentCase || !this.currentCase.fields) return false
		return Object.keys(this.currentCase.fields)
			.some(fieldId => this.currentCase.fields[fieldId].field.dataType === "user-role")
	}

	private async mounted (): Promise<void> {
		if (this.showScheduleInfo) {
			try {
				this.schedule = await api.getSchedule(this.currentCase.scheduling?.scheduleId!)
			} catch (e) {
				// The schedule does not exist
			}
		}
	}

	private async onCaseChanged () {
		if (this.caseUpdating) {
			this.caseUpdateQueued = true
			return
		}

		if (this.currentCase.id) {
			// Add/Remove cadence sourceType if the case is the initially created cadence
			if (this.showCadence && !this.currentCase.cadenceId) {
				if (this.currentCase.cadenceEnabled) {
					this.currentCase.sourceType = SourceType.Cadence
				} else {
					this.currentCase.sourceType = null
				}
			}

			this.caseUpdating = true
			await api.saveCase(this.currentCase)
			this.caseUpdating = false

			if (this.caseUpdateQueued) {
				this.caseUpdateQueued = false
				this.onCaseChanged()
			}
		}
	}

	public openEvent (event: CaseEvent) {
		this.setShowCCEvent(event)
	}

	public updateLocationsList () {
		if (this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	public get showCadence (): boolean {
		return (!!this.template && !!this.template.cadenceDetails &&
			this.template.cadenceDetails.cadencePeriod !== null &&
			this.template.cadenceDetails.cadencePeriod !== undefined &&
			this.currentCase.isClosed === false) || this.showCadenceId
	}

	public get showCadenceId (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceId) && !!this.currentCase.cadenceId && !!this.currentCase.cadenceNumber
	}

	public showDisableCadence (): boolean {
		if (!this.template) return false

		const disablePermissions = this.template!.cadenceDetails!.disablePermissions ? this.template!.cadenceDetails!.disablePermissions : []
		return this.showCadence && (!disablePermissions || disablePermissions.length === 0 ||
			disablePermissions.some((t: any) => this.me.teams.find((team: any) => team.id === t.id)))
	}

	public get showBuiltInFields (): boolean {
		return this.showField(this.template, "status") ||
			this.showField(this.template, "severity") ||
			this.showField(this.template, "affectedAreas") ||
			this.showField(this.template, "tags") ||
			this.showField(this.template, "links")
	}

	public get CadenceIdEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceId)
	}

	get cadenceRunningTotalsFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceRunningTotals)
	}

	get userRoleTimeAsHoursAndMinutesFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserRoleTimeAsHoursMinutes)
	}

	/**
	 * Returns the running total sections for the current cadence instance (if applicable) with their fields and values.
	 * Returned as LabeledValue<sectionName, LabeledValue<fieldName, fieldValue>[]>[]
	 */
	get cadenceRunningTotalSections (): LabeledValue[] {
		if (!this.cadenceRunningTotalsFeatureIsEnabled) return []
		if (!this.caseCadenceInstance?.runningTotalValues) return []

		const rtSections = this.template?.cadenceDetails?.runningTotalSectionFields ?? null
		if (!rtSections) return []

		const rtSectionNames = Object.keys(rtSections)

		let filledSections: LabeledValue[] = []

		for (const sectionName of rtSectionNames) {
			const sectionFieldIds = rtSections[sectionName]
			const sectionValues = sectionFieldIds.map((fieldId: string) => {
				const templateField = this.template.fields.firstOrDefault(field => field.field.id === fieldId)
				if (!templateField) return null

				if (!(templateField.field.id! in this.caseCadenceInstance!.runningTotalValues!)) {
					return null
				}

				const rtValue = cloneDeep(this.caseCadenceInstance!.runningTotalValues![templateField.field.id!])
				rtValue.value = parseFloat(Number(rtValue.value).toFixed(2)).toString()

				if (templateField.field.dataType === "user-role") {
					if (this.userRoleTimeAsHoursAndMinutesFeatureIsEnabled) {
						rtValue.value = this.timeInHoursAndMinutes(rtValue.value)
					} else {
						// display decimal hours
						rtValue.value = rtValue.value + " hours"
					}
				}

				return {
					label: templateField.field.title,
					value: rtValue
				}
			}).filter((v: any) => v !== null)

			if (sectionValues.length > 0) {
				filledSections.push({
					label: sectionName,
					value: sectionValues
				})
			}
		}

		return filledSections
	}

	get displayCadenceRunningTotals (): boolean {
		return this.cadenceRunningTotalsFeatureIsEnabled && this.cadenceRunningTotalSections.length > 0
	}

	private get caseFieldsNotValidated (): CaseField[] {
		if (!this.currentCase || !this.currentCase.fields) {
			return []
		}

		let allFields: CaseField[] = []
		if (this.currentCase && this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (this.currentCase) {
					allFields.push(this.currentCase.fields![fieldId])
				}
			})
		}

		return allFields.filter(field => this.fieldRequired(field))
	}

	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.caseFiles.filter(caseFile => caseFile.fieldId === id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	private get caseActionsValidated (): boolean {
		if (!this.currentCase || !this.currentCase.actions) {
			return true
		}

		const isCase: boolean = this.currentCase.isCase
		return !this.currentCase.actions.some(action => ((action.requiredRecord && !isCase) || (action.requiredCase && isCase)) &&
															action.completedBy === null)
	}

	public get userRoleLocationsEmpty (): string | null {
		if (!this.currentCase || !this.currentCase.fields) return null
		if (!this.getIsFeatureEnabled(TenantFeatures.UserRoleLocation)) return null

		const outstandingUserRoleLocations =
			Object.keys(this.currentCase.fields) // For each field ID
				.map(fieldId => this.currentCase!.fields![fieldId]) // Pull field
				.filter(field => { // Find only user role fields with a value
					return field.field.dataType === "user-role" &&
						field.value != null &&
						field.field.extensions
				})
				.map(field => { // Convert each field to a list of users in which location is not set
					const value = field.value as UserRole
					const userRole = field.field.extensions as UserRoleDefinition

					if (userRole.subroles && userRole.subroles.length > 0) {
						return value.subRoles
							? value.subRoles.map(subRoleValue => {
								if (!subRoleValue.users || subRoleValue.users.length === 0) return []
								const subRole = userRole.subroles!.find(sr => sr.title === subRoleValue.title)
								if (!subRole || !subRole.allowTimeTracking || !subRole.allowLocationTracking) return []

								return this.locationMissingUsers(subRoleValue.users, field.field.title)
							}).flat()
							: []
					} else if (userRole.allowTimeTracking && userRole.allowLocationTracking) {
						return this.locationMissingUsers(value.users, field.field.title)
					}

					return []
				}).flat() // Smoosh list of lists down to one list
		if (outstandingUserRoleLocations.length === 0) return null // No-one missing location

		// Turn to string
		return "Location missing for " + outstandingUserRoleLocations.map(l => `${l.user} (${l.field})`).join(", ")
	}

	private locationMissingUsers (users: CaseUser[] | null, fieldTitle: string): { field: string, user: string }[] {
		return users ? users
			.filter((u: CaseUser) => u.location === null || u.location === undefined)
			.map((u: CaseUser) => {
				return { field: fieldTitle, user: u.name }
			})
			: []
	}

	public get canClose (): boolean {
		return (!this.caseFieldsNotValidated || this.caseFieldsNotValidated.length === 0) && this.caseActionsValidated && !this.userRoleLocationsEmpty
	}

	private closeCase () {
		if (this.canClose) {
			this.closeModalShown = true
		}
	}

	private confirmClose () {
		this.$emit("closeCase")
		this.closeModalShown = false
	}

	private cancelCloseCase () {
		this.$emit("cancelCloseCase")
	}

	get canEditClosedChanges (): boolean {
		if (this.template && this.template.editClosedPermissions && this.me) {
			return this.template.editClosedPermissions.length === 0 ||
					this.template.editClosedPermissions.some((teamId: any) => this.me.teams.find((team: any) => team.id === teamId))
		}
		return false
	}

	get caseRecordName (): string {
		return this.currentCase.isCase
			? this.getCaseName
			: this.getRecordName
	}

	get showCloseCaseButton () {
		return this.currentCase && this.currentCase.id && this.canEditCurrentCase && this.getIsFeatureEnabled(TenantFeatures.SidebarCloseButton) && !this.currentCase.pendingClosure
	}

	get showCancelCloseCaseButton () {
		return this.currentCase && this.currentCase.id && this.canEditCurrentCase && this.getIsFeatureEnabled(TenantFeatures.SidebarCloseButton) && !this.currentCase.isClosed && this.currentCase.pendingClosure
	}

	get getCreatedDetails () {
		const showCreatedBy = this.currentCase.created && (
			this.currentCase.createdBy ||
			(
				this.currentCase.scheduling?.scheduleId &&
				this.currentCase.scheduling.scheduleType !== ScheduleType.Cadence
			)
		)

		if (showCreatedBy) {
			return "Created By: "
		}

		return "Created at: "
	}

	get getClosedDetails () {
		return this.currentCase.closed && this.currentCase.closedBy ? "Closed By: " : "Closed at: "
	}

	get getEditDetails () {
		return this.currentCase.lastUpdated && this.currentCase.lastUserUpdatedBy ? "Last Edit By: " : "Last Edit at: "
	}

	public async copyCaseDetails () {
		if (this.currentCase.caseNumber && this.currentCase.title) {
			const caseDetatils = `Title: ${this.currentCase.title}\n${this.getCaseName}: #${this.currentCase.caseNumber}\nUrl: ${window.location.href}`
			const html = `<p>Title: ${this.currentCase.title}</p><p>${this.getCaseName}: #${this.currentCase.caseNumber}</p><p>Url: <a href="${window.location.href}"> ${window.location.href}</a></p>`
			const type = "text/html"
			const blob = new Blob([html], { type: "text/html" })
			const blob2 = new Blob([caseDetatils], { type: "text/plain" })
			try {
				// @ts-ignore
				await navigator.clipboard.write([new ClipboardItem({ [type]: blob, "text/plain": blob2 })])
				this.copiedItems = "Copied"
			} catch (err) {
			}
		}
	}

	get showConsolidatedInfo () {
		return this.getIsFeatureEnabled(TenantFeatures.ConsolidatedRecordInfo) && this.currentCase && this.currentCase.id
	}

	get getCopyTooltip () {
		return this.copiedItems
	}

	get showScheduleInfo () {
		return !!(this.getIsFeatureEnabled(TenantFeatures.CaseScheduleInfo) &&
			this.currentCase.scheduling && this.currentCase.scheduling.scheduleId) &&
			this.currentCase.scheduling.scheduleType !== ScheduleType.Cadence
	}
}

